import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  addDays,
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  getDaysInMonth,
  isAfter,
  isBefore,
  isEqual,
  isSameMonth,
  startOfDay,
  startOfMonth,
  startOfToday,
  startOfWeek,
} from "date-fns";
import {
  AlertCircle,
  CalendarRange,
  ChevronLeft,
  ChevronRight,
  Download,
  Loader2,
  Search,
  Sparkles,
  TrendingUp,
} from "lucide-react";
import { toast } from "react-hot-toast";
import * as XLSX from "xlsx-js-style";
import { getUserTimeZone } from "../utils/timezone";

type BudgetingTab = "revenue" | "expenses";

interface CustomerProfileRow {
  id: string;
  name: string;
  billDate: number;
  createdAt?: string | null;
  genesysQueueNames: string[];
  billOveragesSeparately?: boolean;
  overageBillDate?: number | null;
}

interface BillingJobRecord {
  id: string;
  status: string;
  queueName?: string | null;
  totalAmount?: number | string | null;
  customerProfileId?: string | null;
  customerProfile?: { id?: string | null } | null;
  metadata?: {
    queues?: BillingJobQueueMetadata[];
    customerProfileId?: string | null;
  } | null;
  createdAt?: string;
}

interface BillingJobQueueMetadata {
  invoiceDate?: string | null;
  invoiceCreated?: boolean | null;
  totalAmount?: number | string | null;
  billed?: { totalAmount?: number | string | null } | null;
}

interface BillDateProjection {
  date: string;
  depositDate: string;
  periodStart: string;
  periodEnd: string;
  type: "next" | "future";
  actualMinutes: number;
  projectedMinutes: number;
  totalMinutes: number;
  billedMinutes: number;
  amount: number;
  revenueSubtype?: "minimum" | "overage";
}

interface CustomerRevenueProjection {
  customerId: string;
  customerName: string;
  billDates: BillDateProjection[];
}

interface CustomerGridCell {
  amount: number;
  source: "actual" | "next" | "projected";
  projection?: BillDateProjection;
}

const currencyFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const formatCurrency = (value: number) => currencyFormatter.format(value || 0);

const safeNumber = (value: unknown, fallback = 0): number => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : fallback;
};

const normalizeBillingDay = (billDate: number | null | undefined, reference: Date) => {
  const day = Number.isFinite(Number(billDate))
    ? Math.max(1, Math.floor(Number(billDate)))
    : 1;
  const daysInMonth = getDaysInMonth(reference);
  return Math.min(day, daysInMonth);
};

const toDateKey = (value: Date) => format(value, "yyyy-MM-dd");

/** Bill date + 1 day; pushed to Monday if it falls on a weekend. */
const toDepositDate = (billDate: Date): Date => {
  const d = addDays(startOfDay(billDate), 1);
  const dow = d.getDay();
  if (dow === 6) return addDays(d, 2); // Sat → Mon
  if (dow === 0) return addDays(d, 1); // Sun → Mon
  return d;
};

// Fixed pixel widths for the two frozen left columns.
// These must match the style={{ width }} values on th/td below.
const COL_CUSTOMER = 210;
const COL_NEXT_BILL = 96;

interface CalendarDatePickerProps {
  id?: string;
  value: Date;
  onChange: (date: Date) => void;
}

function CalendarDatePicker({ id, value, onChange }: CalendarDatePickerProps) {
  const [open, setOpen] = useState(false);
  const [month, setMonth] = useState<Date>(() => startOfMonth(value));
  const containerRef = useRef<HTMLDivElement | null>(null);
  const today = useMemo(() => startOfToday(), []);

  useEffect(() => { setMonth(startOfMonth(value)); }, [value]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current?.contains(e.target as Node)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [open]);

  const daysInView = useMemo(() => {
    const start = startOfWeek(month, { weekStartsOn: 0 });
    const end = endOfWeek(endOfMonth(month), { weekStartsOn: 0 });
    return eachDayOfInterval({ start, end });
  }, [month]);

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        id={id}
        onClick={() => setOpen((p) => !p)}
        className="flex items-center gap-2 border border-gray-300 rounded-md text-sm py-2 px-3 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        <CalendarRange className="h-4 w-4 text-gray-400" />
        <span className="font-medium text-gray-800">{format(value, "yyyy-MM-dd")}</span>
      </button>

      {open && (
        /* z-50 so the calendar popup always sits above the sticky table cells */
        <div className="absolute z-50 mt-2 w-72 rounded-lg border border-gray-200 bg-white shadow-xl">
          <div className="flex items-center justify-between px-3 py-2 border-b border-gray-100">
            <button
              type="button"
              onClick={() => setMonth((p) => addMonths(p, -1))}
              className="p-1 rounded-md text-gray-500 hover:bg-gray-100"
              aria-label="Previous month"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="text-sm font-semibold text-gray-900">{format(month, "MMMM yyyy")}</span>
            <button
              type="button"
              onClick={() => setMonth((p) => addMonths(p, 1))}
              className="p-1 rounded-md text-gray-500 hover:bg-gray-100"
              aria-label="Next month"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
          <div className="grid grid-cols-7 gap-1 px-3 pt-2 pb-1 text-center text-[11px] font-semibold text-gray-400">
            {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((d) => <span key={d}>{d}</span>)}
          </div>
          <div className="grid grid-cols-7 gap-1 px-3 pb-3">
            {daysInView.map((day) => {
              const sel = isEqual(startOfDay(day), startOfDay(value));
              const cur = isSameMonth(day, month);
              const tod = isEqual(startOfDay(day), today);
              return (
                <button
                  key={toDateKey(day)}
                  type="button"
                  onClick={() => { onChange(startOfDay(day)); setOpen(false); }}
                  className={`h-9 w-full rounded-md text-sm transition-colors
                    ${!cur ? "text-gray-300" : sel ? "bg-blue-600 text-white" : "text-gray-700 hover:bg-gray-100"}
                    ${tod && !sel ? "ring-1 ring-blue-500" : ""}`}
                  aria-label={format(day, "MMMM d, yyyy")}
                >
                  {format(day, "d")}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export function Budgeting() {
  const [activeTab, setActiveTab] = useState<BudgetingTab>("revenue");

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Budgeting</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Forward-looking revenue view — actual minutes to date + forecasted remaining days.
          </p>
        </div>
        <div className="flex items-center gap-1.5 text-sm text-gray-400">
          <CalendarRange className="h-4 w-4" />
          <span>Rolling 12-month horizon</span>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
        {/* Tabs */}
        <div className="flex border-b border-gray-200">
          {(["revenue", "expenses"] as BudgetingTab[]).map((tab) => {
            const active = activeTab === tab;
            return (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                disabled={tab === "expenses"}
                title={tab === "expenses" ? "Coming soon" : undefined}
                className={`px-5 py-3.5 text-sm font-medium border-b-2 transition-colors ${
                  active
                    ? "border-blue-600 text-blue-700 bg-blue-50/40"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {tab === "revenue" ? "Revenue" : "Expenses (coming soon)"}
              </button>
            );
          })}
        </div>

        <div className="p-5">
          {activeTab === "revenue" ? (
            <RevenueDashboard />
          ) : (
            <div className="flex items-center gap-3 py-4 text-sm text-gray-500">
              <Sparkles className="h-4 w-4 text-blue-400 shrink-0" />
              Expenses view is coming soon.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function RevenueDashboard() {
  const [customers, setCustomers] = useState<CustomerProfileRow[]>([]);
  const [billingJobs, setBillingJobs] = useState<BillingJobRecord[]>([]);
  const [revenueProjections, setRevenueProjections] = useState<CustomerRevenueProjection[]>([]);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [loadingJobs, setLoadingJobs] = useState(false);
  const [loadingProjections, setLoadingProjections] = useState(false);
  const [timelineStart, setTimelineStart] = useState<Date>(() => startOfToday());
  const [searchTerm, setSearchTerm] = useState("");
  const [sortKey, setSortKey] = useState<"name" | "billDate" | "depositDate">("depositDate");
  const [refreshing, setRefreshing] = useState(false);

  const today = useMemo(() => startOfToday(), []);
  const timelineEnd = useMemo(() => startOfDay(addMonths(timelineStart, 12)), [timelineStart]);

  const loadCustomers = useCallback(async () => {
    setLoadingCustomers(true);
    try {
      const params = new URLSearchParams({ isActive: "true", limit: "500", clientTimeZone: getUserTimeZone() });
      const res = await fetch(`/api/v1/customer-profiles?${params}`);
      if (!res.ok) throw new Error("Failed to load customers");
      const data = await res.json();
      if (!data.success) throw new Error(data.error || "Unable to load customers");
      const rows: CustomerProfileRow[] = Array.isArray(data.data)
        ? data.data.map((item: any) => ({
            id: item.id,
            name: item.qboCompanyName || item.qboDisplayName || item.qboGivenName || "Unknown",
            createdAt: item.createdAt,
            billDate: item.billDate ?? 1,
            genesysQueueNames: Array.isArray(item.genesysQueueNames) ? item.genesysQueueNames : [],
            billOveragesSeparately: item.billOveragesSeparately ?? false,
            overageBillDate: item.overageBillDate ?? null,
          }))
        : [];
      setCustomers(rows);
      return rows;
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Unable to load customers");
      return [];
    } finally {
      setLoadingCustomers(false);
    }
  }, []);

  const loadRevenueProjections = useCallback(async () => {
    setLoadingProjections(true);
    try {
      const res = await fetch("/api/v1/budgeting/revenue-projection?months=12", {
        headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` },
      });
      if (!res.ok) throw new Error("Failed to load revenue projections");
      const data = await res.json();
      if (!data.success) throw new Error(data.error || "Unable to load revenue projections");
      setRevenueProjections(Array.isArray(data.data) ? data.data : []);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Unable to load revenue projections");
    } finally {
      setLoadingProjections(false);
    }
  }, []);

  const getBillingDateForJob = useCallback((job: BillingJobRecord): Date | null => {
    const queueMeta: BillingJobQueueMetadata[] = Array.isArray(job.metadata?.queues) ? job.metadata!.queues! : [];
    const rawDate =
      queueMeta.find((q) => q?.invoiceCreated !== false && q?.invoiceDate)?.invoiceDate ||
      queueMeta.find((q) => q?.invoiceDate)?.invoiceDate ||
      job.createdAt;
    if (!rawDate) return null;
    const parsed = startOfDay(new Date(rawDate));
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }, []);

  const loadBillingJobs = useCallback(async () => {
    setLoadingJobs(true);
    try {
      const aggregated: BillingJobRecord[] = [];
      let page = 1, totalPages = 1, stopEarly = false;
      while (page <= totalPages && !stopEarly) {
        const params = new URLSearchParams({ limit: "100", status: "COMPLETED", page: String(page) });
        const res = await fetch(`/api/v1/billing/jobs?${params}`);
        if (!res.ok) throw new Error("Failed to load billing jobs");
        const data = await res.json();
        if (!data.success) throw new Error(data.error || "Unable to load billing jobs");
        const jobs: BillingJobRecord[] = Array.isArray(data.data) ? data.data : [];
        aggregated.push(...jobs);
        if (data.pagination?.pages) totalPages = data.pagination.pages;
        const oldest = jobs[jobs.length - 1];
        const d = oldest ? getBillingDateForJob(oldest) : null;
        if (d && isBefore(d, timelineStart)) stopEarly = true;
        page++;
      }
      setBillingJobs(aggregated);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Unable to load billing jobs");
    } finally {
      setLoadingJobs(false);
    }
  }, [getBillingDateForJob, timelineStart]);

  const refreshAll = useCallback(async () => {
    setRefreshing(true);
    await Promise.all([loadCustomers(), loadRevenueProjections()]);
    setRefreshing(false);
  }, [loadCustomers, loadRevenueProjections]);

  useEffect(() => { void loadCustomers(); void loadRevenueProjections(); }, [loadCustomers, loadRevenueProjections]);
  useEffect(() => { if (customers.length > 0) void loadBillingJobs(); }, [customers, loadBillingJobs]);

  const projectionsByCustomerDate = useMemo(() => {
    const map: Record<string, Record<string, BillDateProjection>> = {};
    for (const cp of revenueProjections) {
      map[cp.customerId] = {};
      for (const bd of cp.billDates) {
        // Key by deposit date so the cell lands on the correct cashflow column
        map[cp.customerId][bd.depositDate] = bd;
      }
    }
    return map;
  }, [revenueProjections]);

  const queueToCustomerId = useMemo(() => {
    const map = new Map<string, string>();
    customers.forEach((c) => c.genesysQueueNames.filter(Boolean).forEach((q) => map.set(q.toLowerCase(), c.id)));
    return map;
  }, [customers]);

  const timelineDates = useMemo(
    () => eachDayOfInterval({ start: timelineStart, end: timelineEnd }),
    [timelineStart, timelineEnd]
  );

  const billingDatesByCustomer = useMemo(() => {
    const result: Record<string, Date[]> = {};
    customers.forEach((customer) => {
      const occurrences: Date[] = [];
      for (let mo = 0; mo <= 12; mo++) {
        const ref = addMonths(timelineStart, mo);
        const billDate = startOfDay(new Date(ref.getFullYear(), ref.getMonth(), normalizeBillingDay(customer.billDate, ref)));
        // Convert to deposit date: bill date + 1 day, skipping weekends
        const depositDate = toDepositDate(billDate);
        if (!isBefore(depositDate, timelineStart) && !isAfter(depositDate, addMonths(timelineEnd, 0))) {
          occurrences.push(depositDate);
        }
        // When overages are billed separately, also mark the overage deposit date
        if (customer.billOveragesSeparately && customer.overageBillDate) {
          const overageBillDay = Math.max(1, Math.floor(Number(customer.overageBillDate) || 1));
          const normalizedOverageDay = Math.min(overageBillDay, getDaysInMonth(ref));
          const overageBillDate = startOfDay(new Date(ref.getFullYear(), ref.getMonth(), normalizedOverageDay));
          const overageDepositDate = toDepositDate(overageBillDate);
          if (
            !isBefore(overageDepositDate, timelineStart) &&
            !isAfter(overageDepositDate, addMonths(timelineEnd, 0)) &&
            !occurrences.some((d) => isEqual(d, overageDepositDate))
          ) {
            occurrences.push(overageDepositDate);
          }
        }
      }
      result[customer.id] = occurrences.sort((a, b) => a.getTime() - b.getTime());
    });
    return result;
  }, [customers, timelineStart, timelineEnd]);

  const actualsByCustomerDate = useMemo(() => {
    const map: Record<string, Record<string, number>> = {};
    billingJobs.filter((j) => j.status === "COMPLETED").forEach((job) => {
      const queueName = job.queueName?.toLowerCase() || "";
      const customerId =
        job.customerProfileId || job.customerProfile?.id || job.metadata?.customerProfileId || queueToCustomerId.get(queueName);
      if (!customerId) return;
      const billingDate = getBillingDateForJob(job);
      if (!billingDate || isBefore(billingDate, timelineStart) || isAfter(billingDate, timelineEnd)) return;
      const queueMeta: BillingJobQueueMetadata[] = Array.isArray(job.metadata?.queues) ? job.metadata!.queues! : [];
      const invoiced = queueMeta.filter((q) => q?.invoiceCreated !== false);
      const forAmount = invoiced.length > 0 ? invoiced : queueMeta;
      const amount =
        forAmount.length > 0
          ? forAmount.reduce((s, q) => s + safeNumber(q?.billed?.totalAmount ?? q?.totalAmount), 0)
          : safeNumber(job.totalAmount);
      // Place the actual amount on the deposit date (bill date + 1, skipping weekends)
      const depositDate = toDepositDate(billingDate);
      const key = toDateKey(depositDate);
      if (!map[customerId]) map[customerId] = {};
      map[customerId][key] = (map[customerId][key] || 0) + amount;
    });
    return map;
  }, [billingJobs, getBillingDateForJob, queueToCustomerId, timelineStart, timelineEnd]);

  const customerCreatedAtMap = useMemo(() => {
    const m: Record<string, Date> = {};
    customers.forEach((c) => {
      if (!c.createdAt) return;
      const d = startOfDay(new Date(c.createdAt));
      if (!Number.isNaN(d.getTime())) m[c.id] = d;
    });
    return m;
  }, [customers]);

  const filteredCustomers = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    const list = term ? customers.filter((c) => c.name.toLowerCase().includes(term)) : customers.slice();
    return list.sort((a, b) => {
      if (sortKey === "name") return a.name.localeCompare(b.name);
      if (sortKey === "billDate") {
        // Sort by the billing day-of-month number (1, 5, 9, etc.)
        return (a.billDate || 1) - (b.billDate || 1);
      }
      // depositDate — sort by next deposit date in the timeline
      const an = billingDatesByCustomer[a.id]?.[0];
      const bn = billingDatesByCustomer[b.id]?.[0];
      if (an && bn) return an.getTime() - bn.getTime();
      return an ? -1 : bn ? 1 : 0;
    });
  }, [billingDatesByCustomer, customers, searchTerm, sortKey]);

  const getCellValue = (customerId: string, date: Date): CustomerGridCell | null => {
    const dateKey = toDateKey(date);
    const actual = actualsByCustomerDate[customerId]?.[dateKey];
    if (actual !== undefined) return { amount: actual, source: "actual" };
    if (isBefore(date, today)) return null;
    const created = customerCreatedAtMap[customerId];
    if (created && isBefore(date, created)) return null;
    const proj = projectionsByCustomerDate[customerId]?.[dateKey];
    if (!proj) return null;
    return { amount: proj.amount, source: proj.type === "next" ? "next" : "projected", projection: proj };
  };

  const isLoading = loadingCustomers || loadingJobs || loadingProjections || refreshing;

  const downloadExcel = () => {
    if (filteredCustomers.length === 0) return;

    // ── Styles ──
    const headerStyle = {
      font: { bold: true, color: { rgb: "FFFFFF" } },
      fill: { fgColor: { rgb: "1E3A5F" }, patternType: "solid" },
      alignment: { horizontal: "center", vertical: "center", wrapText: true },
      border: { bottom: { style: "thin", color: { rgb: "CCCCCC" } } },
    };
    const actualStyle = {
      font: { bold: true, color: { rgb: "065F46" } },
      fill: { fgColor: { rgb: "D1FAE5" }, patternType: "solid" },
      alignment: { horizontal: "center" },
      numFmt: '"$"#,##0.00',
    };
    const nextStyle = {
      font: { bold: true, color: { rgb: "1E40AF" } },
      fill: { fgColor: { rgb: "DBEAFE" }, patternType: "solid" },
      alignment: { horizontal: "center" },
      numFmt: '"$"#,##0.00',
    };
    const projectedStyle = {
      font: { color: { rgb: "92400E" } },
      fill: { fgColor: { rgb: "FEF3C7" }, patternType: "solid" },
      alignment: { horizontal: "center" },
      numFmt: '"$"#,##0.00',
    };
    const customerStyle = {
      font: { bold: true },
      alignment: { vertical: "center" },
    };
    const subStyle = {
      font: { color: { rgb: "9CA3AF" }, sz: 9 },
      alignment: { vertical: "center" },
    };

    // Billing date columns that have data for at least one customer
    const activeDates = timelineDates.filter((date) =>
      filteredCustomers.some((c) => {
        const billingDates = billingDatesByCustomer[c.id] || [];
        return billingDates.some((d) => isEqual(d, date));
      })
    );

    // ── Header row ──
    const headerRow: any[] = [
      { v: "Customer", s: headerStyle },
      { v: "Bill Day", s: headerStyle },
      { v: "Next Deposit", s: headerStyle },
    ];
    for (const date of activeDates) {
      headerRow.push({ v: format(date, "MMM d, yyyy"), s: headerStyle });
    }

    // ── Data rows (one per customer) ──
    const dataRows: any[][] = filteredCustomers.map((customer) => {
      const billingDates = billingDatesByCustomer[customer.id] || [];
      const nextBill = billingDates.find((d) => !isBefore(d, startOfToday()));

      const row: any[] = [
        { v: customer.name, s: customerStyle },
        { v: `Day ${customer.billDate}`, s: subStyle },
        { v: nextBill ? format(nextBill, "MMM d, yyyy") : "—", s: subStyle },
      ];

      for (const date of activeDates) {
        const cell = getCellValue(customer.id, date);
        const isBillingDay = billingDates.some((d) => isEqual(d, date));

        if (!isBillingDay || !cell) {
          row.push({ v: "", s: {} });
        } else if (cell.source === "actual") {
          row.push({ v: cell.amount, t: "n", s: actualStyle });
        } else if (cell.source === "next") {
          row.push({ v: cell.amount, t: "n", s: nextStyle });
        } else {
          row.push({ v: cell.amount, t: "n", s: projectedStyle });
        }
      }
      return row;
    });

    // ── Column totals row ──
    const totalsRow: any[] = [
      { v: "TOTAL", s: { font: { bold: true }, fill: { fgColor: { rgb: "F3F4F6" }, patternType: "solid" } } },
      { v: "", s: {} },
      { v: "", s: {} },
    ];
    for (const date of activeDates) {
      let total = 0;
      for (const customer of filteredCustomers) {
        const cell = getCellValue(customer.id, date);
        if (cell) total += cell.amount;
      }
      totalsRow.push({
        v: total > 0 ? total : "",
        t: total > 0 ? "n" : "s",
        s: {
          font: { bold: true },
          fill: { fgColor: { rgb: "F3F4F6" }, patternType: "solid" },
          alignment: { horizontal: "center" },
          numFmt: '"$"#,##0.00',
        },
      });
    }

    // ── Legend rows ──
    const legendRows: any[][] = [
      [],
      [
        { v: "Legend", s: { font: { bold: true } } },
        { v: "Billed (actual)", s: { ...customerStyle, fill: { fgColor: { rgb: "D1FAE5" }, patternType: "solid" }, font: { color: { rgb: "065F46" }, bold: true } } },
        { v: "Next bill (actual + projected)", s: { ...customerStyle, fill: { fgColor: { rgb: "DBEAFE" }, patternType: "solid" }, font: { color: { rgb: "1E40AF" }, bold: true } } },
        { v: "Projected (forecast only)", s: { ...customerStyle, fill: { fgColor: { rgb: "FEF3C7" }, patternType: "solid" }, font: { color: { rgb: "92400E" } } } },
      ],
    ];

    const allRows = [headerRow, ...dataRows, totalsRow, ...legendRows];
    const ws = XLSX.utils.aoa_to_sheet(allRows);

    // Column widths
    ws["!cols"] = [
      { wch: 30 }, // Customer
      { wch: 10 }, // Bill Day
      { wch: 16 }, // Next Deposit
      ...activeDates.map(() => ({ wch: 16 })),
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Revenue Budget");

    const fileName = `revenue-budget-${format(new Date(), "yyyy-MM-dd")}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  if (!loadingCustomers && customers.length === 0) {
    return (
      <div className="py-16 text-center">
        <AlertCircle className="h-8 w-8 text-gray-300 mx-auto mb-3" />
        <p className="text-sm text-gray-500">No active customers found for budgeting.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">

      {/* ── Toolbar ─────────────────────────────────────────────────────────── */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        {/* Status */}
        <div className="flex items-center gap-2 min-w-0">
          {isLoading && <Loader2 className="h-3.5 w-3.5 animate-spin text-blue-500 shrink-0" />}
          <span className="text-xs text-gray-400 truncate">
            {refreshing
              ? "Refreshing…"
              : loadingCustomers
              ? "Loading customers…"
              : loadingJobs
              ? "Loading billing history…"
              : loadingProjections
              ? "Building projections…"
              : `${filteredCustomers.length} customer${filteredCustomers.length !== 1 ? "s" : ""}`}
          </span>
        </div>

        {/* Controls */}
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-gray-500 font-medium">From</span>
            <CalendarDatePicker
              id="budgeting-start-date"
              value={timelineStart}
              onChange={(d) => setTimelineStart(d)}
            />
            <button
              type="button"
              onClick={() => setTimelineStart(today)}
              className="text-xs font-medium text-blue-600 hover:text-blue-800"
            >
              Today
            </button>
          </div>

          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-gray-400 pointer-events-none" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search customers"
              className="pl-8 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-40"
            />
          </div>

          <select
            value={sortKey}
            onChange={(e) => setSortKey(e.target.value as "name" | "billDate" | "depositDate")}
            className="border border-gray-300 rounded-md text-sm py-2 px-3 focus:ring-2 focus:ring-blue-500 text-gray-700"
          >
            <option value="name">Sort: Name</option>
            <option value="billDate">Sort: Bill date</option>
            <option value="depositDate">Sort: Deposit date</option>
          </select>

          <button
            onClick={downloadExcel}
            disabled={filteredCustomers.length === 0 || isLoading}
            className="inline-flex items-center gap-1.5 px-3 py-2 border border-green-600 rounded-md text-sm font-medium text-green-700 bg-white hover:bg-green-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <Download className="h-3.5 w-3.5" />
            Export Excel
          </button>

          <button
            onClick={() => { void refreshAll(); }}
            disabled={refreshing}
            className="inline-flex items-center gap-1.5 px-3 py-2 border border-gray-300 rounded-md text-sm text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 transition-colors"
          >
            {refreshing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <TrendingUp className="h-3.5 w-3.5" />}
            Refresh
          </button>
        </div>
      </div>

      {/* ── Legend ──────────────────────────────────────────────────────────── */}
      <div className="flex items-center gap-5 text-xs text-gray-500">
        <span className="flex items-center gap-1.5">
          <span className="h-2 w-2 rounded-full bg-emerald-500 shrink-0" />
          Billed (actual)
        </span>
        <span className="flex items-center gap-1.5">
          <span className="h-2 w-2 rounded-full bg-blue-500 shrink-0" />
          Next bill (actual + projected)
        </span>
        <span className="flex items-center gap-1.5">
          <span className="h-2 w-2 rounded-full bg-amber-400 shrink-0" />
          Projected
        </span>
      </div>

      {/* ── Table ───────────────────────────────────────────────────────────── */}
      {/*
        The scroll container must use overflow-auto (not just overflow-x-auto)
        with a bounded max-height so that:
          - sticky top-0  works for the header row
          - sticky left-0 works for the frozen columns
          - both happen inside this box, never conflicting with the app navbar

        Z-index layers (all local to this stacking context):
          z-30  corner header cells  (sticky top + left)
          z-20  date header cells    (sticky top only)
          z-10  frozen body cells    (sticky left only)
      */}
      <div className="border border-gray-200 rounded-xl overflow-auto max-h-[calc(100vh-320px)] min-h-[200px]">
        <table className="min-w-full border-collapse text-sm">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">

              {/* ── Customer (corner: sticky top + left) ── */}
              <th
                className="sticky top-0 left-0 z-30 bg-gray-50 px-4 py-3 text-left text-xs font-semibold text-gray-600 whitespace-nowrap border-r border-gray-200"
                style={{ width: COL_CUSTOMER, minWidth: COL_CUSTOMER }}
              >
                Customer
              </th>

              {/* ── Next bill (corner: sticky top + left) ── */}
              <th
                className="sticky top-0 z-30 bg-gray-50 px-4 py-3 text-left text-xs font-semibold text-gray-600 whitespace-nowrap"
                style={{
                  left: COL_CUSTOMER,
                  width: COL_NEXT_BILL,
                  minWidth: COL_NEXT_BILL,
                  boxShadow: "3px 0 6px -2px rgba(0,0,0,0.08)",
                }}
              >
                Deposit
              </th>

              {/* ── Date columns (sticky top only) ── */}
              {timelineDates.map((date) => {
                const isToday = isEqual(date, today);
                return (
                  <th
                    key={toDateKey(date)}
                    className={`sticky top-0 z-20 px-3 py-3 text-center text-xs font-semibold whitespace-nowrap ${
                      isToday ? "bg-blue-50 text-blue-600" : "bg-gray-50 text-gray-600"
                    }`}
                  >
                    {isToday ? "Today" : format(date, "MMM d")}
                  </th>
                );
              })}
            </tr>
          </thead>

          <tbody className="divide-y divide-gray-100">
            {filteredCustomers.map((customer) => {
              const billingDates = billingDatesByCustomer[customer.id] || [];
              const nextBill = billingDates.find((d) => !isBefore(d, timelineStart));

              return (
                <tr
                  key={customer.id}
                  className="bg-white even:bg-gray-50/50 hover:bg-blue-50/30 transition-colors group"
                >
                  {/* ── Customer cell (sticky left) ── */}
                  <td
                    className="sticky left-0 z-10 bg-inherit px-4 py-3 whitespace-nowrap border-r border-gray-100 group-hover:bg-blue-50/30"
                    style={{ width: COL_CUSTOMER, minWidth: COL_CUSTOMER }}
                  >
                    <div className="font-medium text-gray-900 text-sm leading-snug">{customer.name}</div>
                    <div className="text-xs text-gray-400 mt-0.5">
                      Day {customer.billDate}
                      {customer.genesysQueueNames.length > 0 && (
                        <> · {customer.genesysQueueNames.length} {customer.genesysQueueNames.length === 1 ? "queue" : "queues"}</>
                      )}
                    </div>
                  </td>

                  {/* ── Next bill cell (sticky left) ── */}
                  <td
                    className="sticky z-10 bg-inherit px-4 py-3 whitespace-nowrap font-medium text-gray-700 group-hover:bg-blue-50/30"
                    style={{
                      left: COL_CUSTOMER,
                      width: COL_NEXT_BILL,
                      minWidth: COL_NEXT_BILL,
                      boxShadow: "3px 0 6px -2px rgba(0,0,0,0.08)",
                    }}
                  >
                    {nextBill ? format(nextBill, "MMM d") : <span className="text-gray-300 text-xs">—</span>}
                  </td>

                  {/* ── Date cells ── */}
                  {timelineDates.map((date) => {
                    const cell = getCellValue(customer.id, date);
                    const isBillingDay = billingDates.some((d) => isEqual(d, date));
                    const isToday = isEqual(date, today);
                    const baseCellCls = `px-3 py-3 text-center ${isToday ? "bg-blue-50/20" : ""}`;

                    if (!isBillingDay) {
                      return <td key={`${customer.id}-${toDateKey(date)}`} className={baseCellCls} />;
                    }

                    if (!cell) {
                      return (
                        <td key={`${customer.id}-${toDateKey(date)}`} className={baseCellCls}>
                          {loadingProjections
                            ? <Loader2 className="h-3 w-3 animate-spin text-blue-300 mx-auto" />
                            : <span className="text-gray-200 text-xs">—</span>}
                        </td>
                      );
                    }

                    const isActual = cell.source === "actual";
                    const isNext = cell.source === "next";

                    const tooltip = isNext && cell.projection
                      ? `Period: ${cell.projection.periodStart} → ${cell.projection.periodEnd}\nActual: ${cell.projection.actualMinutes.toFixed(1)} mins\nProjected: ${cell.projection.projectedMinutes.toFixed(1)} mins\nBilled: ${cell.projection.billedMinutes.toFixed(1)} mins`
                      : !isActual && cell.projection
                      ? `Period: ${cell.projection.periodStart} → ${cell.projection.periodEnd}\nProjected: ${cell.projection.billedMinutes.toFixed(1)} mins`
                      : undefined;

                    return (
                      <td
                        key={`${customer.id}-${toDateKey(date)}`}
                        className={baseCellCls}
                        title={tooltip}
                      >
                        <div className="inline-flex flex-col items-center gap-1">
                          <span className="text-sm font-semibold text-gray-900">
                            {formatCurrency(cell.amount)}
                          </span>
                          <span
                            className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full leading-tight ${
                              isActual
                                ? "bg-emerald-100 text-emerald-700"
                                : isNext
                                ? "bg-blue-100 text-blue-700"
                                : "bg-amber-100 text-amber-700"
                            }`}
                          >
                            {isActual ? "billed" : isNext ? "next bill" : "projected"}
                          </span>
                          {!isActual && cell.projection?.revenueSubtype && (
                            <span
                              className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full leading-tight ${
                                cell.projection.revenueSubtype === "minimum"
                                  ? "bg-indigo-100 text-indigo-700"
                                  : "bg-orange-100 text-orange-700"
                              }`}
                            >
                              {cell.projection.revenueSubtype === "minimum" ? "Min" : "Overage"}
                            </span>
                          )}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              );
            })}

            {filteredCustomers.length === 0 && !loadingCustomers && (
              <tr>
                <td
                  colSpan={2 + timelineDates.length}
                  className="px-4 py-10 text-center text-sm text-gray-400"
                >
                  No customers match the current filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
